import { z } from "zod";
import { EncounterSoapNoteSchema } from "./encounter-documentation.js";
import { LetterTypeSchema } from "./letters.js";
import { ModelReleasePinSchema } from "./model-governance.js";
import { IdSchema } from "./primitives.js";
export const ENCOUNTER_DISCUSSION_PROMPT_VERSION = "encounter-discussion-v1";
const requestText = z.string().trim().min(1).max(1_500);
const scope = { siteId: IdSchema, patientId: IdSchema };
export const EncounterDiscussionHistoryEntrySchema = z
    .object({
    role: z.enum(["user", "assistant"]),
    content: z.string().trim().min(1).max(12_000),
})
    .strict();
export const EncounterDiscussionRequestSchema = z
    .object({
    ...scope,
    message: z.string().trim().min(1).max(8_000),
    noteSections: EncounterSoapNoteSchema.strict(),
    expectedEncounterVersion: z.number().int().nonnegative().optional(),
    history: z.array(EncounterDiscussionHistoryEntrySchema).max(8).default([]),
})
    .strict();
/** These values open the existing editor. Empty fields stay incomplete; nothing is signed. */
export const EncounterDiscussionPrescriptionFieldsSchema = z
    .object({
    medicationDisplay: z.string().trim().min(1).max(200),
    sig: z.string().trim().max(500),
    quantity: z.string().trim().max(40),
    quantityUnit: z.string().trim().max(80),
    refills: z.string().trim().max(40),
    indication: z.string().trim().max(500),
    substitutionAllowed: z.boolean(),
})
    .strict();
/** Discussion offers the same editable workflows as commands, never chart writes or execution. */
export const EncounterDiscussionDraftSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("prescription"),
        text: requestText,
        fields: EncounterDiscussionPrescriptionFieldsSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal("orders"),
        text: requestText,
        category: z.enum(["laboratory", "imaging"]),
        testNames: z.array(z.string().trim().min(1).max(240)).min(1).max(25),
    })
        .strict(),
    z
        .object({
        kind: z.literal("referral"),
        text: requestText,
        specialty: z.string().trim().min(1).max(200).optional(),
        urgency: z.enum(["routine", "soon", "urgent"]).optional(),
    })
        .strict(),
    z.object({ kind: z.literal("education"), text: requestText }).strict(),
    z
        .object({
        kind: z.literal("letter"),
        text: requestText,
        letterType: LetterTypeSchema,
        dateText: z.string().trim().min(1).max(240).optional(),
        wording: z.string().trim().min(1).max(4_000).optional(),
    })
        .strict(),
    z
        .object({
        kind: z.literal("follow_up"),
        text: requestText,
        timing: z.string().trim().min(1).max(240).optional(),
    })
        .strict(),
    z.object({ kind: z.literal("needs_staff"), text: requestText }).strict(),
]);
export const EncounterDiscussionActionSchema = z
    .object({
    id: z.string().trim().min(1).max(100),
    title: z.string().trim().min(1).max(200),
    rationale: z.string().trim().min(1).max(1_500),
    draft: EncounterDiscussionDraftSchema,
})
    .strict();
export const EncounterDiscussionSourceSchema = z
    .object({
    id: z.string().trim().min(1).max(100),
    label: z.string().trim().min(1).max(200),
    text: z.string().min(1).max(60_000),
})
    .strict();
export const EncounterDiscussionContentSchema = z
    .object({
    answer: z.string().trim().min(1).max(12_000),
    actions: z.array(EncounterDiscussionActionSchema).max(8),
    limitations: z.array(z.string().trim().min(1).max(1_000)).max(8),
    evidence: z
        .array(z
        .object({
        sourceId: z.string().trim().min(1).max(100),
        quote: z.string().trim().min(1).max(2_000),
    })
        .strict())
        .max(12),
})
    .strict();
export const EncounterDiscussionContextSchema = z
    .object({
    encounterVersion: z.number().int().nonnegative(),
    fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    assembledAt: z.string().datetime(),
})
    .strict();
export const EncounterDiscussionResponseSchema = EncounterDiscussionContentSchema.extend({
    model: z.string().trim().min(1).max(160),
    promptVersion: z.literal(ENCOUNTER_DISCUSSION_PROMPT_VERSION),
    /** Server-selected model release; the actual task prompt version remains separate. */
    modelRelease: ModelReleasePinSchema.optional(),
    context: EncounterDiscussionContextSchema,
}).strict();
export const EncounterDiscussionContextCheckRequestSchema = z
    .object({
    ...scope,
    noteSections: EncounterSoapNoteSchema.strict(),
    contextFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
})
    .strict();
export const EncounterDiscussionContextCheckResponseSchema = z
    .object({ valid: z.literal(true) })
    .strict();
//# sourceMappingURL=encounter-discussion.js.map